import api, { RemoteApi } from '@disco/api/server'
import {
  UPLOAD_DISCO_USER_LOGIN,
  UPLOAD_DISCO_USER_LOGIN_FAIL,
  UPLOAD_DISCO_USER_LOGIN_REQUIRE_MFA,
  UPLOAD_DISCO_USER_LOGIN_SUCCESS,
  UPLOAD_INSERT_TEMP_ITEMS,
  UPLOAD_REMOVE_TEMP_ITEM,
  UPLOAD_SEND_PLAYLIST,
  UPLOAD_SEND_PLAYLIST_FAIL,
  UPLOAD_SEND_PLAYLIST_SUCCESS,
  UPLOAD_SET_DISCO_PLAYLIST,
  UPLOAD_SET_DISCO_USER,
  UPLOAD_UPDATE_TEMP_ITEM,
} from '@disco/constants/inbox-action-types'
import { uploadFile } from '@disco/utils/upload'
import type { GroupId } from 'types/group'
import type { DiscoPlaylist } from 'types/inbox'
import type { GetState, Thunk } from 'types/redux'
import type { TempItem, TempTrack } from 'types/shortlist'
import type { TrackId } from 'types/track'
import type { LoginDetails, User } from 'types/user'
import { mapResponseToReducerObjects } from '@disco/state-management/actions/PlaylistTrackActions'
import { getLoginErrorFromServerResponse } from '@disco/state-management/actions/UserActions'
type PlaylistSendFunction = (api: Record<string, any>) => Promise<any>

const send =
  (
    api: RemoteApi,
    sendFunction: PlaylistSendFunction,
    options?: { shouldKeepUploadState: boolean },
  ): Thunk =>
  async dispatch => {
    dispatch({
      type: UPLOAD_SEND_PLAYLIST,
    })

    try {
      await api.getCSRFToken()
      await sendFunction(api)
      dispatch({
        type: UPLOAD_SEND_PLAYLIST_SUCCESS,
        shouldKeepUploadState: options?.shouldKeepUploadState,
      })
      window.scrollTo(0, 0)
    } catch (error) {
      dispatch({
        type: UPLOAD_SEND_PLAYLIST_FAIL,
        error: error,
      })
    }
  }

/**
 * Sends a Disco playlist to recipients via email.
 * @param playlist - Playlist object with metadata and recipient information
 * @returns Thunk that sends the playlist and handles success/error states
 */
export const sendDiscoPlaylist =
  (
    playlist: Partial<
      DiscoPlaylist & {
        message: string | null | undefined
        territory_ids?: GroupId[] | null | undefined
      }
    >,
    options?: { shouldKeepUploadState: boolean },
  ): Thunk =>
  dispatch => {
    if (!playlist?.business) {
      return dispatch({
        type: UPLOAD_SEND_PLAYLIST_FAIL,
        error: 'Playlist business is missing',
      })
    }

    const senderApi = new RemoteApi()
    senderApi.host = `https://${playlist.business.hostname}`
    return dispatch(
      send(
        senderApi,
        api => {
          const email = {
            id: playlist.id,
            manual: false,
            subject: playlist.name,
            message: playlist.message,
            territory_ids: playlist.territory_ids,
            recipients: [
              {
                type: 'business',
                id: window.business_data.id,
                group_id: window.group_data.id,
              },
            ],
          }
          return api.emailPlaylist(email)
        },
        options,
      ),
    )
  }
/**
 * Sends a playlist with specified metadata and track IDs.
 * @param playlist - Playlist data including sender info, name, message, and track IDs
 * @returns Thunk that sends the playlist to the server
 */
export const sendPlaylist = (
  playlist: {
    sender_name: string
    sender_email: string
    playlist_name: string
    message: string
    track_ids: TrackId[]
  },
  options?: {
    shouldKeepUploadState: boolean
  },
): Thunk =>
  send(
    api,
    api => api.sendPlaylist(window.group_data.create_playlist_url, playlist),
    options,
  )
/**
 * Inserts temporary items into the upload queue.
 * @param tempItems - Array of temporary track items to add to upload queue
 * @returns Action object to insert temp items
 */
export function insertUploadTempItems(tempItems: TempItem[]): {
  tempItems: Array<TempItem>
  type: string
} {
  return {
    type: UPLOAD_INSERT_TEMP_ITEMS,
    tempItems: tempItems,
  }
}
/**
 * Removes a temporary item from the upload queue.
 * @param tempItem - Temporary track item to remove from upload queue
 * @returns Action object to remove temp item
 */
export function removeUploadTempItem(tempItem: TempTrack): {
  tempItem: TempTrack
  type: string
} {
  return {
    type: UPLOAD_REMOVE_TEMP_ITEM,
    tempItem: tempItem,
  }
}
/**
 * Updates a temporary item in the upload queue.
 * @param tempItem - Temporary track item with updated data
 * @returns Action object to update temp item
 */
export function updateUploadTempItem(tempItem: TempTrack): {
  tempItem: TempTrack
  type: string
} {
  return {
    type: UPLOAD_UPDATE_TEMP_ITEM,
    tempItem: tempItem,
  }
}

const getPolicyUrl = (file: File, policyUrls) => {
  if (file.size / (1024 * 1024) > 10) return policyUrls['TEMPORARY_CREDENTIALS']
  return policyUrls['PRESIGNED_POST']
}

/**
 * Uploads a file to Disco with progress tracking and error handling.
 * @param csrfToken - CSRF token for authentication
 * @param tempItem - Temporary item containing file and metadata
 * @param abortSignal - Signal to abort the upload operation
 * @returns Thunk that uploads the file and handles all upload events
 */
export function uploadFileToDisco(
  csrfToken: string,
  tempItem: TempItem,
  abortSignal: AbortSignal,
): (
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
) => Promise<any> {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    return uploadFile(
      tempItem.file,
      csrfToken,
      tempItem.tempId,
      {
        onInfoParse(info: TempTrack) {
          dispatch(updateUploadTempItem(info))
        },

        // TODO: we don't check for duplicates in public uploads, handle properly if it will be enabled
        // onDuplicateFound(track, tempTrack) {
        // },
        onUploadProgress(tempTrack: TempTrack) {
          dispatch(updateUploadTempItem(tempTrack))
        },

        onUploadError(tempTrack: TempTrack) {
          dispatch(removeUploadTempItem(tempTrack))
        },

        onUploadDone(track, tempTrack) {
          const { title, artist, album } = tempTrack
          const adjustedTrack = track.uploaded
            ? track
            : { ...track, name: title, artist, album, ext: tempItem.ext }
          const normalizedTrack = mapResponseToReducerObjects(
            dispatch,
            getState,
            adjustedTrack,
          )
          // dispatch(removeUploadTempItem(tempTrack))
          const update = { ...tempTrack, ...normalizedTrack }
          const { file, progress, skip, ...rest } = update
          dispatch(updateUploadTempItem(rest as TempTrack))
        },
      },
      window?.group_data?.upload_policy_urls &&
        getPolicyUrl(tempItem.file, window.group_data.upload_policy_urls),
      {},
      abortSignal,
    )
  }
}
/**
 * Sets the current Disco playlist for upload operations.
 * @param playlist - Disco playlist to set or null to clear
 * @returns Action object to set disco playlist with analytics tracking
 */
export const setDiscoPlaylist = (
  playlist: DiscoPlaylist | null | undefined,
): {
  meta: {
    mixpanel: {
      event: string
    }
  } | void
  playlist: DiscoPlaylist | null | undefined
  type: string
} => ({
  type: UPLOAD_SET_DISCO_PLAYLIST,
  playlist,
  meta: playlist
    ? {
        mixpanel: {
          event: 'Send your own playlist for upload (Inbox)',
        },
      }
    : undefined,
})
/**
 * Sets the current Disco user for upload operations.
 * @param user - User object to set or null to clear
 * @returns Action object to set disco user
 */
export const setDiscoUser = (
  user: User | null | undefined,
): {
  type: string
  user: User | null | undefined
} => ({
  type: UPLOAD_SET_DISCO_USER,
  user,
})
/**
 * Authenticates a user with remote Disco instance for upload operations.
 * @param loginDetails - User login credentials
 * @param host - Remote host to authenticate against
 * @returns Thunk that handles authentication flow including MFA support
 */
export const authenticate =
  (loginDetails: LoginDetails, host: string): Thunk =>
  async dispatch => {
    const remoteApi = new RemoteApi()
    remoteApi.host = `https://${host}`

    try {
      dispatch({
        type: UPLOAD_DISCO_USER_LOGIN,
      })
      const authResponse = await remoteApi.postUserAuth(loginDetails)

      if (authResponse.status !== 'success') {
        if (authResponse.reason === 'mfa_required') {
          return dispatch({
            type: UPLOAD_DISCO_USER_LOGIN_REQUIRE_MFA,
            mfaType: authResponse.mfa_type,
          })
        }

        return dispatch({
          type: UPLOAD_DISCO_USER_LOGIN_FAIL,
          error: getLoginErrorFromServerResponse(authResponse),
        })
      }

      return dispatch({
        type: UPLOAD_DISCO_USER_LOGIN_SUCCESS,
        user: authResponse.user_data,
      })
    } catch (error) {
      dispatch({
        type: UPLOAD_DISCO_USER_LOGIN_FAIL,
        error: error,
      })
    }
  }
